import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { Box, Divider, Popover, type PopoverProps, Stack, useTheme } from '@mui/material'
import { ThemeProvider } from '@mui/system'

import { Button, Icon, TextField, Typography } from '~components/internal'
import { internalLightTheme } from '~createInternalTheme'
import { type RootState } from '~redux/rootReducer'
import { spacing } from '~tokens/internal'
import { getValidPersistedChat } from '~utils/chatPersistence'

const getSubmitterStorageKey = (organizationId: string): string =>
  `poli_chat_submitter_${organizationId}`

export const ResponseNegativeFeedbackPopover: React.FC<
  PopoverProps & {
    submitFeedback: (feedback: string, submitter?: string) => void
    organizationId?: string
    chatPersistenceMinutes?: number | null
  }
> = ({ onClose, submitFeedback, organizationId, chatPersistenceMinutes, ...props }) => {
  const isAuthenticated = useSelector((state: RootState) => state?.auth?.isAuthenticated)
  const userName = useSelector((state: RootState) => state?.auth?.user?.name)

  const savedSubmitter =
    !isAuthenticated &&
    organizationId &&
    getValidPersistedChat(organizationId, chatPersistenceMinutes)
      ? (localStorage.getItem(getSubmitterStorageKey(organizationId)) ?? undefined)
      : undefined
  const showSubmitterField = !isAuthenticated && !savedSubmitter

  const [submitter, setName] = useState<string | undefined>(userName ?? savedSubmitter)
  const [feedback, setFeedback] = useState('')
  const ambientTheme = useTheme()

  const onSubmit = (): void => {
    if (!isAuthenticated && submitter && organizationId) {
      localStorage.setItem(getSubmitterStorageKey(organizationId), submitter)
    }

    submitFeedback(feedback, submitter)
    onClose?.({}, 'backdropClick')
    setFeedback('')
  }

  return (
    <ThemeProvider theme={internalLightTheme}>
      <Popover
        onClose={onClose}
        {...props}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        slotProps={{ paper: { sx: { width: 320 } } }}
      >
        <Box sx={{ padding: spacing.xs }}>
          <Stack direction='row' spacing={spacing.xxs} sx={{ alignItems: 'center' }}>
            <Icon name='thumbDown' color={ambientTheme.palette.warning.main} fill={0} />
            <Typography component='p' variant='h5'>
              What went wrong?
            </Typography>
          </Stack>
        </Box>
        <Divider />
        <Stack spacing={spacing.xs} sx={{ padding: spacing.xs }}>
          {showSubmitterField && (
            <TextField
              autoFocus
              label='Name'
              value={submitter}
              onChange={(e) => setName(e.target.value)}
            />
          )}
          <TextField
            autoFocus={!showSubmitterField}
            multiline
            rows={3}
            label='What should the AI have said?'
            placeholder='The ideal answer - include any relevant links'
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant='text'
              onClick={() => {
                onClose?.({}, 'backdropClick')
                setFeedback('')
              }}
            >
              Cancel
            </Button>
            <Button disabled={!feedback.trim()} onClick={onSubmit}>
              Submit
            </Button>
          </Box>
        </Stack>
      </Popover>
    </ThemeProvider>
  )
}
